"use strict";
/**
  * @reach/utils v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */
// src/can-use-dom.ts

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e2) { throw _e2; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e3) { didErr = true; err = _e3; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function canUseDOM() {
  return !!(typeof window !== "undefined" && window.document && window.document.createElement);
} // src/clone-valid-element.ts


import { cloneElement, isValidElement } from "react";

function cloneValidElement(element, props) {
  for (var _len = arguments.length, children = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    children[_key - 2] = arguments[_key];
  }

  return isValidElement(element) ? cloneElement.apply(void 0, [element, props].concat(children)) : element;
} // src/compose-event-handlers.ts


function composeEventHandlers(theirHandler, ourHandler) {
  return function (event) {
    theirHandler && theirHandler(event);

    if (!event.defaultPrevented) {
      return ourHandler(event);
    }
  };
} // src/compose-refs.ts


import { useCallback } from "react"; // src/type-check.ts

function isBoolean(value) {
  return typeof value === "boolean";
}

function isFunction(value) {
  return !!(value && {}.toString.call(value) == "[object Function]");
}

function isNumber(value) {
  return typeof value === "number" && !isNaN(value);
}

function isString(value) {
  return typeof value === "string";
} // src/compose-refs.ts


function assignRef(ref, value) {
  if (ref == null) return;

  if (isFunction(ref)) {
    ref(value);
  } else {
    try {
      ref.current = value;
    } catch (error) {
      throw new Error("Cannot assign value \"".concat(value, "\" to ref \"").concat(ref, "\""));
    }
  }
}

function useComposedRefs() {
  for (var _len2 = arguments.length, refs = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    refs[_key2] = arguments[_key2];
  }

  return useCallback(function (node) {
    var _iterator = _createForOfIteratorHelper(refs),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var ref = _step.value;
        assignRef(ref, node);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }, refs);
} // src/owner-document.ts


function getOwnerDocument(element) {
  return canUseDOM() ? element ? element.ownerDocument : document : null;
}

function getOwnerWindow(element) {
  var ownerDocument = getOwnerDocument(element);
  return ownerDocument ? ownerDocument.defaultView || window : null;
} // src/computed-styles.ts


function getComputedStyles(element) {
  var ownerWindow = getOwnerWindow(element);

  if (ownerWindow) {
    return ownerWindow.getComputedStyle(element, null);
  }

  return null;
}

function getComputedStyle(element, styleProp) {
  var _getComputedStyles;

  return ((_getComputedStyles = getComputedStyles(element)) === null || _getComputedStyles === void 0 ? void 0 : _getComputedStyles.getPropertyValue(styleProp)) || null;
} // src/context.tsx


import * as React from "react";

function createNamedContext(name, defaultValue) {
  var Ctx = React.createContext(defaultValue);
  Ctx.displayName = name;
  return Ctx;
}

function createContext2(rootComponentName, defaultContext) {
  var Ctx = React.createContext(defaultContext);

  function Provider(props) {
    var children = props.children,
        context = _objectWithoutProperties(props, ["children"]);

    var value = React.useMemo(function () {
      return context;
    }, Object.values(context));
    return /* @__PURE__ */React.createElement(Ctx.Provider, {
      value: value
    }, children);
  }

  function useContext2(callerComponentName) {
    var context = React.useContext(Ctx);

    if (context) {
      return context;
    }

    if (defaultContext) {
      return defaultContext;
    }

    throw Error("".concat(callerComponentName, " must be rendered inside of a ").concat(rootComponentName, " component."));
  }

  Ctx.displayName = "".concat(rootComponentName, "Context");
  Provider.displayName = "".concat(rootComponentName, "Provider");
  return [Provider, useContext2];
} // src/dev-utils.ts


import { useRef, useEffect } from "react";

function useControlledSwitchWarning(controlledValue, controlledPropName, componentName) {
  if (true) {
    var controlledRef = useRef(controlledValue != null);
    var nameCache = useRef({
      componentName: componentName,
      controlledPropName: controlledPropName
    });
    useEffect(function () {
      nameCache.current = {
        componentName: componentName,
        controlledPropName: controlledPropName
      };
    }, [componentName, controlledPropName]);
    useEffect(function () {
      var wasControlled = controlledRef.current;
      var _nameCache$current = nameCache.current,
          componentName2 = _nameCache$current.componentName,
          controlledPropName2 = _nameCache$current.controlledPropName;
      var isControlled = controlledValue != null;

      if (wasControlled !== isControlled) {
        console.error("A component is changing an ".concat(wasControlled ? "" : "un", "controlled `").concat(controlledPropName2, "` state of ").concat(componentName2, " to be ").concat(wasControlled ? "un" : "", "controlled. This is likely caused by the value changing from undefined to a defined value, which should not happen. Decide between using a controlled or uncontrolled ").concat(componentName2, " element for the lifetime of the component.\n      More info: https://fb.me/react-controlled-components"));
      }
    }, [controlledValue]);
  }
} // src/get-document-dimensions.ts


function getDocumentDimensions(element) {
  var _ownerDocument$docume, _ownerDocument$docume2;

  var ownerDocument = getOwnerDocument(element);
  var ownerWindow = ownerDocument.defaultView || window;

  if (!ownerDocument) {
    return {
      width: 0,
      height: 0
    };
  }

  return {
    width: (_ownerDocument$docume = ownerDocument.documentElement.clientWidth) !== null && _ownerDocument$docume !== void 0 ? _ownerDocument$docume : ownerWindow.innerWidth,
    height: (_ownerDocument$docume2 = ownerDocument.documentElement.clientHeight) !== null && _ownerDocument$docume2 !== void 0 ? _ownerDocument$docume2 : ownerWindow.innerHeight
  };
} // src/get-scroll-position.ts


function getScrollPosition(element) {
  var ownerWindow = getOwnerWindow(element);

  if (!ownerWindow) {
    return {
      scrollX: 0,
      scrollY: 0
    };
  }

  return {
    scrollX: ownerWindow.scrollX,
    scrollY: ownerWindow.scrollY
  };
} // src/get-scrollbar-offset.ts


function getScrollbarOffset() {
  try {
    if (window.innerWidth > document.documentElement.clientWidth) {
      return window.innerWidth - document.documentElement.clientWidth;
    }
  } catch (err) {}

  return 0;
} // src/is-right-click.ts


function isRightClick(nativeEvent) {
  return "which" in nativeEvent ? nativeEvent.which === 3 : "button" in nativeEvent ? nativeEvent.button === 2 : false;
} // src/make-id.ts


function makeId() {
  for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    args[_key3] = arguments[_key3];
  }

  return args.filter(function (val) {
    return val != null;
  }).join("--");
} // src/noop.ts


function noop() {} // src/use-constant.ts


import { useRef as useRef2 } from "react";

function useConstant(fn) {
  var ref = useRef2();

  if (!ref.current) {
    ref.current = {
      v: fn()
    };
  }

  return ref.current.v;
} // src/use-controlled-state.ts


import { useState, useRef as useRef3, useCallback as useCallback2 } from "react";

function useControlledState(_ref) {
  var controlledValue = _ref.controlledValue,
      defaultValue = _ref.defaultValue,
      _ref$calledFrom = _ref.calledFrom,
      calledFrom = _ref$calledFrom === void 0 ? "A component" : _ref$calledFrom;
  var wasControlled = controlledValue !== void 0;
  var isControlledRef = useRef3(wasControlled);

  if (true) {
    if (!isControlledRef.current && wasControlled) {
      console.warn("".concat(calledFrom, " is changing from controlled to uncontrolled. Components should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled value for the lifetime of the component."));
    }

    if (isControlledRef.current && !wasControlled) {
      console.warn("".concat(calledFrom, " is changing from uncontrolled to controlled. Components should not switch from uncontrolled to controlled (or vice versa). Decide between using a controlled or uncontrolled value for the lifetime of the component."));
    }
  }

  var _useState = useState(isControlledRef.current ? controlledValue : defaultValue),
      _useState2 = _slicedToArray(_useState, 2),
      valueState = _useState2[0],
      setValue = _useState2[1];

  var set = useCallback2(function (n) {
    if (!isControlledRef.current) {
      setValue(n);
    }
  }, []);
  return [isControlledRef.current ? controlledValue : valueState, set];
} // src/use-event-listener.ts


import { useRef as useRef4, useEffect as useEffect2 } from "react";

function useEventListener(eventName, listener) {
  var element = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : window;
  var savedHandler = useRef4(listener);
  useEffect2(function () {
    savedHandler.current = listener;
  }, [listener]);
  useEffect2(function () {
    var isSupported = element && element.addEventListener;

    if (!isSupported) {
      if (true) {
        console.warn("Event listener not supported on the element provided");
      }

      return;
    }

    function eventListener(event) {
      savedHandler.current(event);
    }

    element.addEventListener(eventName, eventListener);
    return function () {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
} // src/use-focus-change.ts


import { useRef as useRef5, useEffect as useEffect3 } from "react";

function useFocusChange() {
  var handleChange = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : console.log;
  var when = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "focus";
  var ownerDocument = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : document;
  var lastActiveElement = useRef5(ownerDocument.activeElement);
  useEffect3(function () {
    lastActiveElement.current = ownerDocument.activeElement;

    function onChange(event) {
      if (lastActiveElement.current !== ownerDocument.activeElement) {
        handleChange(ownerDocument.activeElement, lastActiveElement.current, event);
        lastActiveElement.current = ownerDocument.activeElement;
      }
    }

    ownerDocument.addEventListener(when, onChange, true);
    return function () {
      ownerDocument.removeEventListener(when, onChange);
    };
  }, [when, handleChange, ownerDocument]);
} // src/use-force-update.ts


import { useState as useState2, useCallback as useCallback3 } from "react";

function useForceUpdate() {
  var _useState3 = useState2( /* @__PURE__ */Object.create(null)),
      _useState4 = _slicedToArray(_useState3, 2),
      dispatch = _useState4[1];

  return useCallback3(function () {
    dispatch( /* @__PURE__ */Object.create(null));
  }, []);
} // src/use-isomorphic-layout-effect.ts


import { useEffect as useEffect4, useLayoutEffect } from "react";
var useIsomorphicLayoutEffect = canUseDOM() ? useLayoutEffect : useEffect4; // src/use-lazy-ref.ts

import { useRef as useRef6 } from "react";

function useLazyRef(fn) {
  var isSet = useRef6(false);
  var ref = useRef6();

  if (!isSet.current) {
    isSet.current = true;
    ref.current = fn();
  }

  return ref;
} // src/use-previous.ts


import { useRef as useRef7, useEffect as useEffect5 } from "react";

function usePrevious(value) {
  var ref = useRef7(null);
  useEffect5(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
} // src/use-stable-callback.ts


import { useRef as useRef8, useEffect as useEffect6, useCallback as useCallback4 } from "react";

function createStableCallbackHook(useEffectHook, callback) {
  var callbackRef = useRef8(callback);
  useEffectHook(function () {
    callbackRef.current = callback;
  });
  return useCallback4(function () {
    callbackRef.current && callbackRef.current.apply(callbackRef, arguments);
  }, []);
}

function useStableCallback(callback) {
  return createStableCallbackHook(useEffect6, callback);
}

function useStableLayoutCallback(callback) {
  return createStableCallbackHook(useIsomorphicLayoutEffect, callback);
} // src/use-stateful-ref-value.ts


import { useState as useState3, useCallback as useCallback5 } from "react";

function useStatefulRefValue(ref, initialState) {
  var _useState5 = useState3(initialState),
      _useState6 = _slicedToArray(_useState5, 2),
      state = _useState6[0],
      setState = _useState6[1];

  var callbackRef = useCallback5(function (refValue) {
    ref.current = refValue;
    setState(refValue);
  }, []);
  return [state, callbackRef];
} // src/use-update-effect.ts


import { useRef as useRef9, useEffect as useEffect7 } from "react";

function useUpdateEffect(effect, deps) {
  var mounted = useRef9(false);
  useEffect7(function () {
    if (mounted.current) {
      effect();
    } else {
      mounted.current = true;
    }
  }, deps);
}

export { assignRef, canUseDOM, cloneValidElement, composeEventHandlers, createContext2 as createContext, createNamedContext, getComputedStyle, getComputedStyles, getDocumentDimensions, getOwnerDocument, getOwnerWindow, getScrollPosition, getScrollbarOffset, isBoolean, isFunction, isNumber, isRightClick, isString, makeId, noop, useComposedRefs, useConstant, useControlledState, useControlledSwitchWarning, useEventListener, useFocusChange, useForceUpdate, useIsomorphicLayoutEffect, useLazyRef, usePrevious, useStableCallback, useStableLayoutCallback, useStatefulRefValue, useUpdateEffect };